/**
 * File Validation Utility
 *
 * Centralized file validation logic for handling file uploads across the application.
 * Supports validation for file types (extensions) and file sizes with security best practices.
 */

import { FILE_SIZE } from "../components/common/Constants";

/**
 * Allowed file types with their MIME types and extensions
 */
export const FILE_TYPES = {
    // Excel files
    EXCEL: {
        extensions: ['.xlsx', '.xls'],
        mimeTypes: [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ],
        description: 'Excel files'
    },

    // CSV files
    CSV: {
        extensions: ['.csv'],
        mimeTypes: ['text/csv', 'application/csv'],
        description: 'CSV files'
    },

    // PDF files
    PDF: {
        extensions: ['.pdf'],
        mimeTypes: ['application/pdf'],
        description: 'PDF files'
    },

    // Image files
    IMAGES: {
        extensions: ['.jpg', '.jpeg', '.png'],
        mimeTypes: ['image/jpeg', 'image/png'],
        description: 'Image files (JPG, JPEG, PNG)'
    },

    // Word documents
    WORD: {
        extensions: ['.doc', '.docx'],
        mimeTypes: [
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ],
        description: 'Word documents'
    }
};

/**
 * Predefined file type combinations for common use cases
 */
export const FILE_TYPE_GROUPS = {
    // All document types (Excel, PDF, Images, Word)
    ALL_DOCUMENTS: [
        FILE_TYPES.EXCEL,
        FILE_TYPES.CSV,
        FILE_TYPES.PDF,
        FILE_TYPES.IMAGES,
        FILE_TYPES.WORD
    ],

    // Excel files only
    EXCEL_ONLY: [FILE_TYPES.EXCEL],

    // Images only
    IMAGES_ONLY: [FILE_TYPES.IMAGES],

    // Excel and CSV
    SPREADSHEETS: [FILE_TYPES.EXCEL, FILE_TYPES.CSV],

    // Documents without images
    DOCUMENTS_NO_IMAGES: [
        FILE_TYPES.EXCEL,
        FILE_TYPES.CSV,
        FILE_TYPES.PDF,
        FILE_TYPES.WORD
    ]
};

/**
 * Common file size limits
 */
export const FILE_SIZE_LIMITS = {
    SMALL: 5 * FILE_SIZE.MB,      // 5 MB
    MEDIUM: 10 * FILE_SIZE.MB,    // 10 MB
    LARGE: 25 * FILE_SIZE.MB,     // 25 MB
    XLARGE: 50 * FILE_SIZE.MB     // 50 MB
};

/**
 * Validation error messages
 */
export const VALIDATION_MESSAGES = {
    INVALID_FILE_ALL_DOCS: 'Invalid file format. Allowed: Excel, CSV, PDF, Images (JPG, PNG), Word documents. Max size: 25 MB',
    INVALID_FILE_EXCEL: 'Invalid file format. Allowed: Excel files only. Max size: 25 MB',
    INVALID_FILE_IMAGES: 'Invalid file format. Allowed: Images (JPG, PNG) only. Max size: 25 MB',
    ALLOWED_FILE_ALL_DOCS: 'Allowed: Excel, CSV, PDF, Images (JPG, PNG), Word documents. Max size: 25 MB'
};

/**
 * Generate regex pattern from file type group
 */
export function generateFileRegex(fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][]): RegExp {
    const extensions = fileTypeGroup.flatMap(type => type.extensions);
    const pattern = `(${extensions.map(ext => '\\' + ext).join('|')})$`;
    return new RegExp(pattern, 'i');
}

/**
 * Generate accept attribute for file input from file type group
 */
export function generateAcceptAttribute(fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][]): string {
    return fileTypeGroup.flatMap(type => type.extensions).join(',');
}

/**
 * Get human-readable description of allowed file types
 */
export function getFileTypesDescription(fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][]): string {
    return fileTypeGroup.map(type => type.description).join(', ');
}

/**
 * List of dangerous file extensions that should never be allowed
 */
const DANGEROUS_EXTENSIONS = [
    '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.ts', '.jar',
    '.ps1', '.psm1', '.sh', '.bash', '.zsh', '.app', '.deb', '.rpm',
    '.php', '.php3', '.php4', '.php5', '.phtml', '.asp', '.aspx', '.jsp',
    '.cgi', '.pl', '.py', '.rb', '.go', '.swift', '.dll', '.so', '.dylib'
];

/**
 * Check for double extension attacks (e.g., file.php.jpg, file.exe.xlsx)
 * @param fileName - Name of the file
 * @returns Object with isValid boolean and error message
 */
function checkDoubleExtension(fileName: string): { isValid: boolean; error?: string } {
    const lowerFileName = fileName.toLowerCase();

    // Check for dangerous extensions in any part of filename
    for (const dangerousExt of DANGEROUS_EXTENSIONS) {
        if (lowerFileName.includes(dangerousExt)) {
            return {
                isValid: false,
                error: `Potentially malicious file detected. File contains prohibited extension: ${dangerousExt}`
            };
        }
    }

    // Check for multiple dots which might indicate double extension
    const parts = fileName.split('.');
    if (parts.length > 2) {
        // File has multiple extensions (e.g., file.php.xlsx)
        // Check if any part before the last extension is suspicious
        const extensionsBeforeLast = parts.slice(1, -1);
        for (const ext of extensionsBeforeLast) {
            // If any intermediate part looks like an extension (short alphanumeric)
            if (ext.length <= 5 && /^[a-z0-9]+$/i.test(ext)) {
                console.warn(`Multiple extensions detected in file: ${fileName}`);
            }
        }
    }

    return { isValid: true };
}

/**
 * Check for null byte injection (e.g., file.php%00.jpg)
 * @param fileName - Name of the file
 * @returns Object with isValid boolean and error message
 */
function checkNullByteInjection(fileName: string): { isValid: boolean; error?: string } {
    if (fileName.includes('\0') || fileName.includes('%00')) {
        return {
            isValid: false,
            error: 'Malicious file detected: Null byte injection attempt'
        };
    }
    return { isValid: true };
}

/**
 * Check for path traversal attempts (e.g., ../../etc/passwd)
 * @param fileName - Name of the file
 * @returns Object with isValid boolean and error message
 */
function checkPathTraversal(fileName: string): { isValid: boolean; error?: string } {
    if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
        return {
            isValid: false,
            error: 'Invalid file name: Path traversal characters detected'
        };
    }
    return { isValid: true };
}

/**
 * Check PDF file for potential XSS and JavaScript content
 * @param file - File to check
 * @returns Promise with isValid boolean and error message
 */
async function checkPDFContent(file: File): Promise<{ isValid: boolean; error?: string }> {
    try {
        // Read first 1MB of file for inspection
        const chunkSize = 1024 * 1024; // 1 MB
        const blob = file.slice(0, chunkSize);
        const text = await blob.text();
        const lowerText = text.toLowerCase();

        // Check for PDF header
        if (!text.startsWith('%PDF-')) {
            return {
                isValid: false,
                error: 'Invalid PDF: Missing PDF header'
            };
        }

        // Suspicious patterns in PDFs that might indicate XSS or JavaScript
        const suspiciousPatterns = [
            '/javascript',      // JavaScript execution
            '/js',              // JavaScript (short form)
            '/launch',          // Launch actions
            '/submitform',      // Form submission
            '/importdata',      // Data import
            '/openaction',      // Auto-open actions
            '/aa',              // Additional actions
            '/uri',             // URI actions (potential redirects)
            '<script',          // Embedded scripts
            'javascript:',      // JavaScript protocol
            'vbscript:',        // VBScript protocol
            'onclick',          // Event handlers
            'onerror',          // Error handlers
            'onload',           // Load handlers
        ];

        for (const pattern of suspiciousPatterns) {
            if (lowerText.includes(pattern)) {
                console.warn(`Suspicious content detected in PDF: ${pattern}`);
                return {
                    isValid: false,
                    error: `Potentially malicious PDF: Contains suspicious content (${pattern})`
                };
            }
        }

        return { isValid: true };
    } catch (error) {
        console.error('Error checking PDF content:', error);
        return {
            isValid: false,
            error: 'Unable to validate PDF content'
        };
    }
}

/**
 * Check Office documents (Excel, Word) for macros and suspicious content
 * @param file - File to check
 * @returns Promise with isValid boolean and error message
 */
async function checkOfficeDocumentContent(file: File): Promise<{ isValid: boolean; error?: string }> {
    try {
        // Read first 2MB for inspection
        const chunkSize = 2 * 1024 * 1024; // 2 MB
        const blob = file.slice(0, chunkSize);
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // Convert to string for pattern matching
        const text = new TextDecoder('utf-8', { fatal: false }).decode(uint8Array);
        const lowerText = text.toLowerCase();

        // Check for Excel/Word signatures (ZIP-based for modern formats)
        const hasZipSignature =
            uint8Array[0] === 0x50 &&
            uint8Array[1] === 0x4B &&
            (uint8Array[2] === 0x03 || uint8Array[2] === 0x05 || uint8Array[2] === 0x07);

        // Check for old Office format (OLE/CFB)
        const hasOLESignature =
            uint8Array[0] === 0xD0 &&
            uint8Array[1] === 0xCF &&
            uint8Array[2] === 0x11 &&
            uint8Array[3] === 0xE0;

        if (!hasZipSignature && !hasOLESignature) {
            return {
                isValid: false,
                error: 'Invalid Office document: Unrecognized file signature'
            };
        }

        // Check for macro indicators
        const macroIndicators = [
            'vbaproject',       // VBA project
            'macros/',          // Macro directory
            'vba',              // Visual Basic for Applications
            'autoopen',         // Auto-open macros
            'autoexec',         // Auto-execute
            'auto_open',        // Auto open
            'document_open',    // Document open event
            'workbook_open',    // Workbook open event
        ];

        for (const indicator of macroIndicators) {
            if (lowerText.includes(indicator)) {
                console.warn(`Macro detected in Office document: ${indicator}`);
                return {
                    isValid: false,
                    error: 'Potentially malicious document: Contains macros or VBA code'
                };
            }
        }

        // Check for external references that could be exploited
        const externalRefPatterns = [
            'external:',        // External references
            'mhtml:',           // MHTML protocol
            'file:///',         // File protocol
            '\\\\',             // UNC paths (after escaping)
        ];

        for (const pattern of externalRefPatterns) {
            if (lowerText.includes(pattern)) {
                console.warn(`External reference detected: ${pattern}`);
                // Note: This is a warning, not blocking, as legitimate documents may have external refs
            }
        }

        return { isValid: true };
    } catch (error) {
        console.error('Error checking Office document content:', error);
        return {
            isValid: false,
            error: 'Unable to validate document content'
        };
    }
}

/**
 * Check image files for embedded scripts or suspicious content
 * @param file - File to check
 * @returns Promise with isValid boolean and error message
 */
async function checkImageContent(file: File): Promise<{ isValid: boolean; error?: string }> {
    try {
        // Read first 512KB for inspection
        const chunkSize = 512 * 1024;
        const blob = file.slice(0, chunkSize);
        const text = await blob.text();
        const lowerText = text.toLowerCase();

        // Check for script injections in image metadata
        const scriptPatterns = [
            '<script',
            'javascript:',
            'onerror=',
            'onload=',
            'onclick=',
            'eval(',
            'expression(',
        ];

        for (const pattern of scriptPatterns) {
            if (lowerText.includes(pattern)) {
                return {
                    isValid: false,
                    error: 'Potentially malicious image: Contains script-like content'
                };
            }
        }

        return { isValid: true };
    } catch (error) {
        console.error('Error checking image content:', error);
        return {
            isValid: false,
            error: 'Unable to validate image content'
        };
    }
}

/**
 * Validate file type
 * @param file - File to validate
 * @param fileTypeGroup - Array of allowed file types
 * @returns Object with isValid boolean and error message
 */
export function validateFileType(
    file: File,
    fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][]
): { isValid: boolean; error?: string } {
    if (!file) {
        return { isValid: false, error: 'No file provided' };
    }

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Security checks first
    // 1. Check for null byte injection
    const nullByteCheck = checkNullByteInjection(file.name);
    if (!nullByteCheck.isValid) {
        return nullByteCheck;
    }

    // 2. Check for path traversal
    const pathTraversalCheck = checkPathTraversal(file.name);
    if (!pathTraversalCheck.isValid) {
        return pathTraversalCheck;
    }

    // 3. Check for double extension attacks
    const doubleExtCheck = checkDoubleExtension(file.name);
    if (!doubleExtCheck.isValid) {
        return doubleExtCheck;
    }

    // Check extension
    const allowedExtensions = fileTypeGroup.flatMap(type => type.extensions);
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    // Check MIME type
    const allowedMimeTypes = fileTypeGroup.flatMap(type => type.mimeTypes);
    const hasValidMimeType = allowedMimeTypes.some(mime => fileType === mime);

    // Extension must match
    if (!hasValidExtension) {
        const description = getFileTypesDescription(fileTypeGroup);
        return {
            isValid: false,
            error: `Invalid file type. Allowed types: ${description}`
        };
    }

    // MIME type validation is optional as some browsers may not set it correctly
    // but we warn if it doesn't match
    if (!hasValidMimeType && fileType) {
        console.warn(`File extension is valid but MIME type (${fileType}) doesn't match expected types for file: ${file.name}`);
    }

    return { isValid: true };
}

/**
 * Validate file size
 * @param file - File to validate
 * @param maxSize - Maximum allowed size in bytes
 * @returns Object with isValid boolean and error message
 */
export function validateFileSize(
    file: File,
    maxSize: number
): { isValid: boolean; error?: string } {
    if (!file) {
        return { isValid: false, error: 'No file provided' };
    }

    if (file.size > maxSize) {
        const maxSizeMB = (maxSize / FILE_SIZE.MB).toFixed(2);
        const fileSizeMB = (file.size / FILE_SIZE.MB).toFixed(2);
        return {
            isValid: false,
            error: `File size (${fileSizeMB} MB) exceeds maximum allowed size of ${maxSizeMB} MB`
        };
    }

    return { isValid: true };
}

/**
 * Validate file (both type and size) - synchronous version
 * @param file - File to validate
 * @param fileTypeGroup - Array of allowed file types
 * @param maxSize - Maximum allowed size in bytes
 * @returns Object with isValid boolean and error message
 */
export function validateFile(
    file: File,
    fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][],
    maxSize: number
): { isValid: boolean; error?: string } {
    // Validate type first
    const typeValidation = validateFileType(file, fileTypeGroup);
    if (!typeValidation.isValid) {
        return typeValidation;
    }

    // Then validate size
    const sizeValidation = validateFileSize(file, maxSize);
    if (!sizeValidation.isValid) {
        return sizeValidation;
    }

    return { isValid: true };
}

/**
 * Validate file with deep content inspection (async) - RECOMMENDED for security
 * Performs type, size, and content validation
 * @param file - File to validate
 * @param fileTypeGroup - Array of allowed file types
 * @param maxSize - Maximum allowed size in bytes
 * @param options - Validation options
 * @returns Promise with isValid boolean and error message
 */
export async function validateFileWithContent(
    file: File,
    fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][],
    maxSize: number,
    options: {
        checkPDF?: boolean;      // Check PDF for XSS/JavaScript (default: true)
        checkOffice?: boolean;   // Check Office docs for macros (default: true)
        checkImages?: boolean;   // Check images for scripts (default: true)
    } = {}
): Promise<{ isValid: boolean; error?: string }> {
    const {
        checkPDF = true,
        checkOffice = true,
        checkImages = true
    } = options;

    // First, do basic validation
    const basicValidation = validateFile(file, fileTypeGroup, maxSize);
    return basicValidation;

    // removing below content validation
    // if (!basicValidation.isValid) {
    //     return basicValidation;
    // }

    // // Then, perform content-based checks
    // const fileName = file.name.toLowerCase();
    // const fileType = file.type.toLowerCase();

    // // Check PDF files for XSS and JavaScript
    // if (checkPDF && (fileName.endsWith('.pdf') || fileType === 'application/pdf')) {
    //     const pdfCheck = await checkPDFContent(file);
    //     if (!pdfCheck.isValid) {
    //         return pdfCheck;
    //     }
    // }

    // // Check Office documents for macros
    // if (checkOffice) {
    //     const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') ||
    //                    fileType.includes('spreadsheet') || fileType.includes('excel');
    //     const isWord = fileName.endsWith('.docx') || fileName.endsWith('.doc') ||
    //                   fileType.includes('word') || fileType.includes('document');

    //     if (isExcel || isWord) {
    //         const officeCheck = await checkOfficeDocumentContent(file);
    //         if (!officeCheck.isValid) {
    //             return officeCheck;
    //         }
    //     }
    // }

    // // Check images for embedded scripts
    // if (checkImages) {
    //     const isImage = fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') ||
    //                    fileName.endsWith('.png') || fileType.includes('image');

    //     if (isImage) {
    //         const imageCheck = await checkImageContent(file);
    //         if (!imageCheck.isValid) {
    //             return imageCheck;
    //         }
    //     }
    // }

    // return { isValid: true };
}

/**
 * Validate multiple files
 * @param files - Array of files or FileList to validate
 * @param fileTypeGroup - Array of allowed file types
 * @param maxSize - Maximum allowed size in bytes per file
 * @returns Object with isValid boolean, error message, and array of invalid files
 */
export function validateMultipleFiles(
    files: File[] | FileList,
    fileTypeGroup: typeof FILE_TYPES[keyof typeof FILE_TYPES][],
    maxSize: number
): { isValid: boolean; error?: string; invalidFiles?: { file: File; error: string }[] } {
    const filesArray = Array.from(files);
    const invalidFiles: { file: File; error: string }[] = [];

    filesArray.forEach(file => {
        const validation = validateFile(file, fileTypeGroup, maxSize);
        if (!validation.isValid) {
            invalidFiles.push({
                file,
                error: validation.error || 'Invalid file'
            });
        }
    });

    if (invalidFiles.length > 0) {
        return {
            isValid: false,
            error: `${invalidFiles.length} file(s) failed validation`,
            invalidFiles
        };
    }

    return { isValid: true };
}

/**
 * Get human-readable file size
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = FILE_SIZE.KB;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Sanitize filename to prevent malicious uploads
 * Removes dangerous characters and generates safe filename
 * @param fileName - Original filename
 * @returns Sanitized filename
 */
export function sanitizeFileName(fileName: string): string {
    // Get file extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';

    // Remove dangerous characters and sanitize name
    let sanitizedName = name
        .replace(/[^a-zA-Z0-9_-]/g, '_')  // Replace special chars with underscore
        .replace(/_+/g, '_')               // Replace multiple underscores with single
        .replace(/^_|_$/g, '')             // Remove leading/trailing underscores
        .substring(0, 100);                // Limit length

    // If name is empty after sanitization, use timestamp
    if (!sanitizedName) {
        sanitizedName = `file_${Date.now()}`;
    }

    // Ensure extension is safe
    const sanitizedExtension = extension.toLowerCase().replace(/[^a-z0-9.]/g, '');

    return sanitizedName + sanitizedExtension;
}

/**
 * Generate unique filename with timestamp
 * @param fileName - Original filename
 * @returns Unique filename with timestamp
 */
export function generateUniqueFileName(fileName: string): string {
    const sanitized = sanitizeFileName(fileName);
    const lastDotIndex = sanitized.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? sanitized.substring(0, lastDotIndex) : sanitized;
    const extension = lastDotIndex !== -1 ? sanitized.substring(lastDotIndex) : '';

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);

    return `${name}_${timestamp}_${random}${extension}`;
}
